<template>
  <!-- Pinned below the nav so support stays reachable from a long menu. -->
  <div class="sidebar-footer">
    <button
      type="button"
      class="support-trigger"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      @click="onToggleSupport"
    >
      Need help? <span class="support-trigger-link">Contact support</span>
    </button>

    <Popover ref="popover" @show="isOpen = true" @hide="isOpen = false">
      <div class="support-panel">
        <p class="support-panel-title">Contact TFA Support</p>

        <a class="support-row" :href="`tel:${SUPPORT_PHONE_E164}`">
          <i class="support-row-icon pi pi-phone" />
          <span class="support-row-value">{{ SUPPORT_PHONE_DISPLAY }}</span>
          <button
            type="button"
            class="support-copy"
            :aria-label="`Copy ${SUPPORT_PHONE_DISPLAY}`"
            @click.prevent.stop="copy(SUPPORT_PHONE_DISPLAY)"
          >
            <i class="pi pi-copy" />
          </button>
        </a>

        <a class="support-row" :href="supportMailto">
          <i class="support-row-icon pi pi-envelope" />
          <span class="support-row-value">{{ SUPPORT_EMAIL }}</span>
          <button
            type="button"
            class="support-copy"
            :aria-label="`Copy ${SUPPORT_EMAIL}`"
            @click.prevent.stop="copy(SUPPORT_EMAIL)"
          >
            <i class="pi pi-copy" />
          </button>
        </a>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import Popover from "primevue/popover";
import { useToast } from "primevue/usetoast";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { addMixpanelEvent, EVENT_NAMES } from "@/shared/utils/analytics";

const SUPPORT_PHONE_DISPLAY = "(424) 312-0680";
const SUPPORT_PHONE_E164 = "+14243120680";
const SUPPORT_EMAIL = "support@thefuturesapp.com";

const authStore = useAuthStore();
const { currentOrganization, currentOrganizationId } = useCurrentOrganization();
const toast = useToast();

const popover = ref<InstanceType<typeof Popover> | null>(null);
const isOpen = ref(false);

// Prefill who is writing and which org they're in so support doesn't have to
// spend a round-trip identifying them. Impersonation is deliberately ignored
// for identity: the sender is always the logged-in admin, while the org is
// whichever one they're currently looking at.
const supportMailto = computed(() => {
  const orgName = currentOrganization.value?.name ?? "Unknown organization";
  const subject = `Support request — ${orgName}`;
  const body = [
    "",
    "",
    "— Sent from TFA Admin —",
    `Organization: ${orgName} (ID: ${currentOrganizationId.value ?? "unknown"})`,
    `Admin: ${authStore.user?.name ?? "Unknown"} (${authStore.user?.email ?? "unknown"})`,
  ].join("\n");

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
});

const onToggleSupport = (event: MouseEvent) => {
  if (!isOpen.value) addMixpanelEvent(EVENT_NAMES.Navigation.NavAdminSupport);
  popover.value?.toggle(event);
};

const copy = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.add({
      severity: "success",
      summary: "Copied",
      detail: value,
      life: 2000,
    });
  } catch {
    toast.add({
      severity: "warn",
      summary: "Couldn't copy",
      detail: "Copy it manually instead.",
      life: 3000,
    });
  }
};
</script>

<style scoped>
/* No rule above it: the nav already ends in a divider, and the support line is
   small and centered enough to read as footer chrome on its own. The padding is
   what keeps it off the last menu item when the nav scrolls. */
.sidebar-footer {
  margin-top: auto;
  flex-shrink: 0;
  padding-top: 12px;
}

/* Deliberately not a card and not an icon row. Sized and centered like the
   footer text it is, so it reads as a fallback rather than a fourth nav item.
   Centering matches the copyright line at the bottom of the page body. */
.support-trigger {
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 8px 6px 2px;
  font-size: 0.7rem;
  font-weight: 400;
  color: var(--text-color-secondary);
  text-align: center;
  cursor: pointer;
}

.support-trigger-link {
  color: var(--p-primary-color);
  font-weight: 600;
}

.support-trigger:hover .support-trigger-link,
.support-trigger:focus-visible .support-trigger-link {
  text-decoration: underline;
}

.support-panel {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 260px;
}

.support-panel-title {
  font-weight: 600;
  font-size: 0.8rem;
  margin: 0 0 4px;
}

.support-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 6px;
  text-decoration: none;
  color: inherit;
  font-size: 0.85rem;
}

.support-row:hover {
  background: var(--surface-50);
}

.support-row-icon {
  color: var(--p-primary-color);
  font-size: 14px;
}

.support-row-value {
  flex: 1;
}

.support-copy {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-color-secondary);
  opacity: 0;
  padding: 2px;
}

.support-row:hover .support-copy,
.support-copy:focus-visible {
  opacity: 1;
}

.support-copy:hover {
  color: var(--p-primary-color);
}
</style>
